import React from "react";
import { motion } from "framer-motion";
import paddock69Logo from "@/assets/paddock69-logo.png";

interface Paddock69LogoProps {
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  animated?: boolean;
  showText?: boolean;
}

const sizeConfig = {
  sm: { imgHeight: 36, textSize: "text-[10px]", spacing: "gap-1" },
  md: { imgHeight: 56, textSize: "text-xs", spacing: "gap-2" },
  lg: { imgHeight: 80, textSize: "text-sm", spacing: "gap-3" },
  xl: { imgHeight: 120, textSize: "text-base", spacing: "gap-3" },
  hero: { imgHeight: 180, textSize: "text-lg", spacing: "gap-4" },
};

export const Paddock69Logo = React.forwardRef<HTMLDivElement, Paddock69LogoProps>(
  ({ size = "md", animated = true, showText = true }, ref) => {
    const config = sizeConfig[size];

  return (
    <motion.div
      ref={ref}
      initial={animated ? { opacity: 0 } : undefined}
      animate={animated ? { opacity: 1 } : undefined}
      transition={{ duration: 0.6 }}
      className={`flex flex-col items-center ${config.spacing}`}
    >
      {/* Logo — mix-blend-mode: lighten makes the dark bg transparent */}
      <motion.img
        src={paddock69Logo}
        alt="PADDOCK 69"
        style={{
          height: config.imgHeight,
          maskImage: "radial-gradient(ellipse 60% 55% at center, black 30%, transparent 85%)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 55% at center, black 30%, transparent 85%)",
        }}
        className="w-auto mix-blend-screen select-none"
        draggable={false}
        initial={animated ? { opacity: 0, scale: 0.9 } : undefined}
        animate={animated ? { opacity: 1, scale: 1 } : undefined}
        transition={{ delay: 0.1, duration: 0.5, type: "spring", stiffness: 100 }}
      />

      {showText && (
        <motion.div
          className="flex flex-col items-center"
          initial={animated ? { opacity: 0, y: 6 } : undefined}
          animate={animated ? { opacity: 1, y: 0 } : undefined}
          transition={{ delay: 0.4, duration: 0.4 }}
        >
          <span
            className={`font-display font-black tracking-[0.3em] text-foreground uppercase ${config.textSize}`}
          >
            PADDOCK 69
          </span>
          <motion.div
            className="h-[1.5px] w-full mt-1.5 rounded-full"
            style={{
              background: "linear-gradient(90deg, transparent 0%, hsl(var(--primary)) 30%, hsl(var(--primary)) 70%, transparent 100%)",
              boxShadow: "0 0 12px hsl(var(--primary) / 0.5)",
            }}
            initial={animated ? { scaleX: 0 } : undefined}
            animate={animated ? { scaleX: 1 } : undefined}
            transition={{ delay: 0.6, duration: 0.5 }}
          />
        </motion.div>
      )}
    </motion.div>
  );
});
Paddock69Logo.displayName = "Paddock69Logo";
